import { useEffect, useMemo, useState } from 'react';
import AdminDockerLogLine from './AdminDockerLogLine';
import {
  dockerLogMatchesFilter,
  dockerLogMatchesScanSelection,
  dockerLogMatchesSearch,
  dockerLogSeverity,
  groupDockerLogsByScan,
  LOG_FILTER_MODES,
} from '../../utils/adminDockerLog';
import {
  ADMIN_BTN_GHOST,
  ADMIN_CARD_MUTED,
  ADMIN_INPUT,
} from '../../pages/admin/adminTheme';

const FILTER_LABELS = {
  all: 'All',
  scans: 'Scan entries',
  errors: 'Errors',
  warnings: 'Warnings+',
};

function sessionLabel(sess) {
  const target = sess.target || 'scan';
  const path = String(sess.path || '')
    .replace(/^\//, '')
    .replace('aggregate-', '')
    .replace('-stream', '');
  const short = sess.shortId || sess.id?.slice(-8) || '?';
  return `${path || 'scan'} · ${target} · #${short}`;
}

export default function LogConsole({
  logBoxRef,
  activeLogTab,
  containerLogs,
  logFollowScroll,
  onResumeAutoscroll,
  onLogPanelScroll,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [hideNonMatching, setHideNonMatching] = useState(false);
  const [selectedScanId, setSelectedScanId] = useState('');
  const [groupByScan, setGroupByScan] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const rawLines = containerLogs[activeLogTab] || [];

  const grouped = useMemo(() => groupDockerLogsByScan(rawLines), [rawLines]);

  useEffect(() => {
    setSelectedScanId('');
    setSearchQuery('');
  }, [activeLogTab]);

  const selectScan = (scanId) => {
    const next = scanId || '';
    setSelectedScanId(next);
    // Full id in Search + Hide non-matching hid everything except START/END banners.
    if (next) {
      setSearchQuery('');
      setHideNonMatching(false);
    }
  };

  const visibleLines = useMemo(() => {
    let lastSessionId = null;
    return rawLines.map((line, index) => {
      const matchesFilter = dockerLogMatchesFilter(line, filterMode);
      const matchesSearch = dockerLogMatchesSearch(line, searchQuery);
      const matchesScan = dockerLogMatchesScanSelection(line, selectedScanId || null, grouped, index);
      const hasSearch = Boolean(searchQuery.trim());
      // Text search + "Hide non-matching" must not wipe session lines that lack the typed id.
      const textGate = !hasSearch || !hideNonMatching || matchesSearch;
      const shown = matchesFilter && matchesScan && textGate;
      const dimmed = !matchesFilter || !matchesScan || (hasSearch && !matchesSearch);
      const ordered = grouped.ordered[index];
      const sessionId = ordered?.sessionId || null;
      const isBanner = ordered?.type === 'banner';
      const severity = dockerLogSeverity(line);
      const showDivider =
        groupByScan &&
        !selectedScanId &&
        Boolean(sessionId) &&
        sessionId !== lastSessionId &&
        (isBanner || severity === 'scan_entry' || severity === 'scan_banner');
      if (sessionId) lastSessionId = sessionId;
      return {
        line,
        index,
        shown,
        dimmed,
        severity,
        sessionId,
        isBanner,
        showDivider,
        marker: ordered?.marker || null,
      };
    });
  }, [rawLines, filterMode, searchQuery, hideNonMatching, selectedScanId, grouped, groupByScan]);

  const shownCount = visibleLines.filter((row) => row.shown).length;
  const scanCount = rawLines.filter((l) => {
    const sev = dockerLogSeverity(l);
    return sev === 'scan_entry' || sev === 'scan_banner';
  }).length;
  const errorCount = rawLines.filter((l) => dockerLogSeverity(l) === 'error').length;
  const selectedSession = grouped.sessions.find((s) => s.id === selectedScanId) || null;

  const filterToolbar = (
    <>
      <div className="flex flex-wrap items-end gap-3 md:flex-wrap">
        <label className="min-w-[220px] flex-1">
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            Search logs
          </span>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="SCAN START, [scan:...], email=, ERROR..."
            className={ADMIN_INPUT}
          />
        </label>
        <label className="w-full max-w-xs flex-1">
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            Filter by scan
          </span>
          <select
            value={selectedScanId}
            onChange={(e) => selectScan(e.target.value)}
            className={ADMIN_INPUT}
            data-testid="admin-scan-filter"
          >
            <option value="">All scans ({grouped.sessions.length})</option>
            {[...grouped.sessions].reverse().map((sess) => (
              <option key={sess.id} value={sess.id}>
                {sessionLabel(sess)}
                {sess.lineCount ? ` · ${sess.lineCount} lines` : ''}
                {sess.elapsedMs != null ? ` · ${Math.round(sess.elapsedMs / 1000)}s` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 pb-1 text-[11px] text-slate-400">
          <input
            type="checkbox"
            checked={groupByScan}
            onChange={(e) => setGroupByScan(e.target.checked)}
            className="rounded border-white/20 bg-slate-950"
          />
          Show scan dividers
        </label>
        <label className="flex items-center gap-2 pb-1 text-[11px] text-slate-400">
          <input
            type="checkbox"
            checked={hideNonMatching}
            onChange={(e) => setHideNonMatching(e.target.checked)}
            className="rounded border-white/20 bg-slate-950"
          />
          Hide non-matching text
        </label>
        {searchQuery.trim() || selectedScanId ? (
          <button
            type="button"
            onClick={() => {
              setSearchQuery('');
              selectScan('');
              setHideNonMatching(false);
            }}
            className={ADMIN_BTN_GHOST}
          >
            Clear
          </button>
        ) : null}
      </div>

      {grouped.sessions.length > 0 ? (
        <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1" data-testid="admin-scan-chip-row">
          {[...grouped.sessions].reverse().slice(0, 12).map((sess) => {
            const active = selectedScanId === sess.id;
            return (
              <button
                key={sess.id}
                type="button"
                title={sessionLabel(sess)}
                onClick={() => selectScan(active ? '' : sess.id)}
                className={`inline-flex max-w-[14rem] shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold transition ${
                  active
                    ? 'border-cyan-400/50 bg-cyan-500/20 text-cyan-50'
                    : 'border-white/10 bg-slate-950/60 text-slate-300 hover:border-cyan-500/30 hover:text-cyan-100'
                }`}
              >
                <span className="truncate">{sess.target || sess.path || 'scan'}</span>
                <span className="font-mono text-[9px] text-slate-500">#{sess.shortId}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1 md:flex-wrap md:overflow-visible">
        {LOG_FILTER_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => setFilterMode(mode)}
            className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-wider ${
              filterMode === mode
                ? 'border-cyan-400/45 bg-cyan-500/20 text-cyan-50'
                : 'border-white/10 bg-slate-950/50 text-slate-400 hover:bg-white/5'
            }`}
          >
            {FILTER_LABELS[mode]}
          </button>
        ))}
        <span className="ml-auto shrink-0 font-mono text-[10px] text-slate-500">
          {shownCount}/{rawLines.length} lines · {grouped.sessions.length} scans · {scanCount}{' '}
          markers · {errorCount} err
        </span>
      </div>
    </>
  );

  return (
    <>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onResumeAutoscroll}
          disabled={logFollowScroll}
          className={`${ADMIN_BTN_GHOST} disabled:cursor-not-allowed disabled:opacity-40`}
        >
          Resume autoscroll
        </button>
        <p className="max-w-md text-[11px] text-slate-500">
          Autoscroll stays on when you are near the bottom of the panel. Scroll up to pause.
        </p>
      </div>

      <div className={`mt-4 space-y-3 ${ADMIN_CARD_MUTED}`}>
        <div className="md:hidden">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={`flex w-full items-center justify-between ${ADMIN_BTN_GHOST}`}
            aria-expanded={filtersOpen}
          >
            Filters & severity
            <span className="font-mono text-slate-500">{filtersOpen ? '[-]' : '[+]'}</span>
          </button>
          {filtersOpen ? <div className="mt-3 space-y-3">{filterToolbar}</div> : null}
        </div>
        <div className="hidden space-y-3 md:block">{filterToolbar}</div>

        {selectedSession ? (
          <div
            className="flex flex-wrap items-center gap-2 rounded-lg border border-cyan-500/35 bg-cyan-950/40 px-3 py-2"
            data-testid="admin-scan-isolate-banner"
          >
            <span className="text-[9px] font-black uppercase tracking-wider text-cyan-200">Isolated scan</span>
            <span className="truncate text-[11px] font-semibold text-slate-100">
              {selectedSession.target || 'unknown target'}
            </span>
            <span className="font-mono text-[10px] text-cyan-300/90">{selectedSession.path}</span>
            <span className="font-mono text-[10px] text-slate-400">
              id={selectedSession.id} · #{selectedSession.shortId} · {selectedSession.lineCount || 0}{' '}
              lines (START→END)
            </span>
            <button
              type="button"
              className="ml-auto text-[10px] font-bold uppercase tracking-wider text-cyan-200 hover:text-cyan-50"
              onClick={() => {
                try {
                  navigator.clipboard?.writeText(selectedSession.id);
                } catch {
                  /* ignore */
                }
              }}
            >
              Copy id
            </button>
          </div>
        ) : (
          <p className="text-[10px] text-slate-500">
            <span className="font-semibold text-fuchsia-300">[SCAN START/END]</span> banners mark each
            investigation. Progress lines use{' '}
            <span className="font-semibold text-cyan-300">[scan:id]</span>. Pick a scan chip or use the
            dropdown to isolate one search.
          </p>
        )}
      </div>

      <div
        ref={logBoxRef}
        onScroll={onLogPanelScroll}
        className="mt-4 min-h-[55vh] max-h-[min(75vh,720px)] overflow-auto rounded-xl border border-white/10 bg-slate-950 p-4 font-mono text-[11px] leading-relaxed shadow-inner shadow-black/40"
        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
      >
        <div className="space-y-0.5">
          {visibleLines.map((row) => {
            if (!row.shown) return null;
            const sess = grouped.sessions.find((s) => s.id === row.sessionId);
            return (
              <div key={`${activeLogTab}-${row.index}-${row.line.slice(0, 48)}`}>
                {row.showDivider && sess ? (
                  <div
                    className="sticky top-0 z-10 my-2 flex flex-wrap items-center gap-2 rounded-lg border border-fuchsia-500/40 bg-fuchsia-950/90 px-2.5 py-1.5 shadow-lg backdrop-blur-sm"
                    data-testid="scan-session-divider"
                  >
                    <span className="rounded bg-fuchsia-500/30 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-fuchsia-100">
                      Scan
                    </span>
                    <span className="truncate text-[11px] font-semibold text-fuchsia-50">
                      {sess.target || 'unknown target'}
                    </span>
                    <span className="truncate text-[10px] text-fuchsia-200/80">{sess.path}</span>
                    <button
                      type="button"
                      className="ml-auto font-mono text-[9px] text-fuchsia-200 hover:text-white"
                      onClick={() => selectScan(sess.id)}
                      title="Isolate this scan"
                    >
                      #{sess.shortId}
                      {sess.elapsedMs != null ? ` · ${Math.round(sess.elapsedMs / 1000)}s` : ''} · isolate
                    </button>
                  </div>
                ) : null}
                <AdminDockerLogLine
                  line={row.line}
                  severity={row.severity}
                  dimmed={row.dimmed && !selectedScanId}
                  searchQuery={searchQuery}
                />
              </div>
            );
          })}
          {rawLines.length === 0 ? (
            <p className="text-slate-500">Waiting for docker log stream…</p>
          ) : shownCount === 0 ? (
            <p className="text-slate-500">No lines match the current filters.</p>
          ) : null}
        </div>
      </div>
    </>
  );
}
